<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Spinner from 'next/spinner/Spinner.vue';
import WgptCustomReportsAPI from 'dashboard/api/wgpt/customReports';
import ReportChart from '../../../components/ReportChart.vue';
import CustomReportDrilldownDrawer from '../../../components/CustomReportDrilldownDrawer.vue';
import {
  buildQueryParamsFor,
  buildChartData,
  buildChartProps,
  hasChartData,
  formatMetricValue,
  formatTableCell,
  buildTableCellTotals,
  formatPeriodLabel,
  buildDimensionLabel,
  buildDrilldownParams,
  describeDrilldownBucket,
  drilldownBucketForClick,
  drilldownBucketForRow,
} from '../../../helpers/reportChartHelpers';

const props = defineProps({
  widget: {
    type: Object,
    required: true,
  },
  // Optional dashboard-level period ({ since, until } ISO strings). When set it
  // overrides the widget's own time range so one filter drives every widget.
  period: {
    type: Object,
    default: null,
  },
  // Lets a click on the chart or a table row list the events behind it. Off on the
  // dashboard editor canvas, where a click selects the widget instead.
  drillable: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const isLoading = ref(false);
const error = ref(null);
const queryResult = ref({});
// The params of the query on screen, so a drilldown lists exactly the events it aggregated.
const lastQueryParams = ref({});
// { params, bucketLabel, bucketValue } of the open drilldown, null while closed.
const drilldown = ref(null);

const report = computed(() => ({
  config: props.widget.report_config || {},
  name: props.widget.report_name,
}));
const metrics = computed(() => report.value.config?.metrics || []);
const reportDefaults = computed(() => report.value.config || {});

const overrides = computed(() => props.widget.overrides || {});

const effectiveTimeRange = computed(
  () => overrides.value.time_range || 'last_30_days'
);
const effectiveGranularity = computed(
  () =>
    overrides.value.time_granularity ||
    reportDefaults.value.default_time_granularity ||
    'day'
);
// Fall back to the report's configured breakdown (default_group_by) unless the
// widget overrides it, so a widget on "Use report default" matches what the
// report viewer shows by default. The 'none' sentinel requests the ungrouped
// Summary view explicitly — without it, a report with a default_group_by could
// never render an ungrouped total on any dashboard.
const effectiveDimension = computed(() => {
  if (overrides.value.dimension === 'none') return '';
  return (
    overrides.value.dimension || reportDefaults.value.default_group_by || ''
  );
});
const effectiveVisualization = computed(
  () =>
    overrides.value.visualization ||
    reportDefaults.value.default_visualization ||
    'bar_chart'
);
const effectiveValueMode = computed(
  () =>
    overrides.value.value_mode ||
    reportDefaults.value.default_value_mode ||
    'absolute'
);

const timeseriesData = computed(() => queryResult.value?.timeseries || []);
const totalsData = computed(() => queryResult.value?.totals || {});
const showSummaryCards = computed(
  () =>
    !!reportDefaults.value.show_summary_cards &&
    !isLoading.value &&
    !error.value &&
    metrics.value.length > 0 &&
    timeseriesData.value.length > 0
);
const hasDimensionData = computed(
  () =>
    !!effectiveDimension.value &&
    timeseriesData.value[0]?.dimension_value !== undefined
);

const isTable = computed(() => effectiveVisualization.value === 'table');

const dimensionLabel = computed(() =>
  buildDimensionLabel(
    queryResult.value?.dimension_labels,
    t('WGPT_CUSTOM_REPORTS.VIEWER.NO_VALUE')
  )
);

const chartData = computed(() =>
  buildChartData({
    timeseries: timeseriesData.value,
    metrics: metrics.value,
    granularity: effectiveGranularity.value,
    hasDimension: hasDimensionData.value,
    visualization: effectiveVisualization.value,
    valueMode: effectiveValueMode.value,
    dimensionLabel: dimensionLabel.value,
  })
);

const chartProps = computed(() =>
  buildChartProps({
    valueMode: effectiveValueMode.value,
    hasDimension: hasDimensionData.value,
    granularity: effectiveGranularity.value,
    metrics: metrics.value,
  })
);

const hasChart = computed(() => hasChartData(chartData.value));

const isNoTimeAggregation = computed(
  () => effectiveGranularity.value === 'none'
);

// Denominators for percentage table cells, matching the chart's normalisation.
const tableCellTotal = computed(() =>
  buildTableCellTotals(
    timeseriesData.value,
    metrics.value,
    hasDimensionData.value && !isNoTimeAggregation.value
  )
);

const tableColumns = computed(() => {
  const cols = [];
  if (!isNoTimeAggregation.value) {
    cols.push({
      key: '__period',
      label: t('WGPT_CUSTOM_REPORTS.VIEWER.PERIOD'),
    });
  }
  if (hasDimensionData.value) {
    cols.push({ key: '__dimension', label: effectiveDimension.value });
  }
  metrics.value.forEach(metric => {
    cols.push({ key: metric.key, label: metric.label || metric.key });
  });
  return cols;
});

// A widget's own time-range override applies while the dashboard filter sits on
// its untouched default; the moment the user explicitly picks a period there, that
// selection overrides every widget.
const appliedPeriod = computed(() => {
  if (!props.period) return null;
  if (overrides.value.time_range && props.period.isDefault) return null;
  return props.period;
});

const queryParams = computed(() =>
  buildQueryParamsFor({
    timeRange: effectiveTimeRange.value,
    granularity: effectiveGranularity.value,
    dimension: effectiveDimension.value,
    since: appliedPeriod.value?.since,
    until: appliedPeriod.value?.until,
  })
);

async function fetchData() {
  if (!props.widget.wgpt_custom_report_id) return;
  isLoading.value = true;
  error.value = null;
  const params = queryParams.value;
  try {
    const { data } = await WgptCustomReportsAPI.query(
      props.widget.wgpt_custom_report_id,
      params
    );
    queryResult.value = data;
    lastQueryParams.value = params;
  } catch (e) {
    error.value = e?.message || t('WGPT_CUSTOM_REPORTS.VIEWER.ERROR');
  } finally {
    isLoading.value = false;
  }
}

// One immediate watcher replaces the onMounted fetch, deduped on the serialised
// params so an equivalent period object arriving after mount (the range picker
// re-emitting the same window) doesn't refetch.
let lastFetchedKey = null;
watch(
  () => [props.widget.wgpt_custom_report_id, JSON.stringify(queryParams.value)],
  ([reportId, key]) => {
    const fetchKey = `${reportId}|${key}`;
    if (fetchKey === lastFetchedKey) return;
    lastFetchedKey = fetchKey;
    fetchData();
  },
  { immediate: true }
);

function openDrilldown(bucket, bucketValue = '') {
  drilldown.value = {
    params: buildDrilldownParams(lastQueryParams.value, bucket),
    bucketLabel: describeDrilldownBucket(
      bucket,
      effectiveGranularity.value,
      dimensionLabel.value
    ),
    bucketValue,
  };
}

function onChartItemClick(click) {
  openDrilldown(
    drilldownBucketForClick(chartData.value, click),
    click.formattedValue
  );
}

function onRowClick(row) {
  if (!props.drillable) return;

  openDrilldown(
    drilldownBucketForRow(row, {
      timeBucketed: !isNoTimeAggregation.value,
      hasDimension: hasDimensionData.value,
    })
  );
}

defineExpose({ refresh: fetchData });
</script>

<template>
  <div class="bg-n-surface-2 rounded-lg p-4 h-full flex flex-col">
    <div class="flex items-start justify-between mb-3">
      <div class="min-w-0">
        <h3 class="text-heading-3 text-n-slate-12 truncate">
          {{ widget.title || report.name }}
        </h3>
        <p v-if="widget.title" class="text-xs text-n-slate-11 truncate">
          {{ report.name }}
        </p>
      </div>
    </div>

    <div v-if="showSummaryCards" class="grid grid-cols-2 gap-2 mb-3">
      <div
        v-for="metric in metrics"
        :key="metric.key"
        class="bg-n-alpha-1 rounded-md p-2"
      >
        <p class="text-xs text-n-slate-11 truncate">
          {{ metric.label || metric.key }}
        </p>
        <p class="text-base font-semibold text-n-slate-12">
          {{ formatMetricValue(totalsData[metric.key]) }}
        </p>
      </div>
    </div>

    <div
      v-if="isLoading"
      class="flex-1 flex items-center justify-center min-h-[200px]"
    >
      <Spinner />
    </div>
    <div
      v-else-if="error"
      class="flex-1 flex items-center justify-center min-h-[200px] text-sm text-n-ruby-11"
    >
      {{ error }}
    </div>
    <div
      v-else-if="!timeseriesData.length"
      class="flex-1 flex items-center justify-center min-h-[200px] text-sm text-n-slate-11"
    >
      {{ t('WGPT_CUSTOM_REPORTS.VIEWER.NO_DATA') }}
    </div>
    <div v-else-if="isTable" class="flex-1 overflow-auto">
      <table class="w-full">
        <thead>
          <tr class="border-b border-n-strong">
            <th
              v-for="col in tableColumns"
              :key="col.key"
              class="text-left py-2 px-3 text-xs font-medium text-n-slate-11"
              :class="col.key.startsWith('__') ? '' : 'text-right'"
            >
              {{ col.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, idx) in timeseriesData"
            :key="idx"
            class="border-b border-n-weak"
            :class="{
              'cursor-pointer hover:bg-n-alpha-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand':
                drillable,
            }"
            :tabindex="drillable ? 0 : undefined"
            @click="onRowClick(row)"
            @keydown.enter.prevent="onRowClick(row)"
          >
            <td
              v-for="col in tableColumns"
              :key="col.key"
              class="py-2 px-3 text-sm text-n-slate-12"
              :class="col.key.startsWith('__') ? '' : 'text-right'"
            >
              <template v-if="col.key === '__period'">
                {{ formatPeriodLabel(row.period, effectiveGranularity) }}
              </template>
              <template v-else-if="col.key === '__dimension'">
                {{ dimensionLabel(row.dimension_value) }}
              </template>
              <template v-else>
                {{
                  formatTableCell(
                    row[col.key],
                    effectiveValueMode,
                    tableCellTotal(row, col.key)
                  )
                }}
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else-if="hasChart" class="flex-1 min-h-[240px]">
      <ReportChart
        :visualization="effectiveVisualization"
        :data="chartData"
        :aria-label="widget.title || report.name"
        :height="240"
        :timeseries="!isNoTimeAggregation"
        :drillable="drillable"
        v-bind="chartProps"
        @item-click="onChartItemClick"
      />
    </div>

    <CustomReportDrilldownDrawer
      v-if="drillable"
      :open="!!drilldown"
      :report-id="widget.wgpt_custom_report_id"
      :title="widget.title || report.name"
      :bucket-label="drilldown?.bucketLabel"
      :bucket-value="drilldown?.bucketValue"
      :params="drilldown?.params"
      @close="drilldown = null"
    />
  </div>
</template>
